import React, { useState } from 'react';
import { X, Plus, AlertCircle, CheckCircle } from 'lucide-react';
import { stcBinService } from '../../../../../../services/stcBinService';
import { CreateStcBinRequest } from '../../../../../../types/stcBin';
import { useAuth } from '../../../../../../contexts/AuthContext';
import { MasterDataLookups } from './types';

interface LocationCreateModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    masterData: MasterDataLookups | null;
}

export const LocationCreateModal: React.FC<LocationCreateModalProps> = ({
    isOpen,
    onClose,
    onSuccess,
    masterData
}) => {
    const { user } = useAuth();

    const [formData, setFormData] = useState<CreateStcBinRequest>({
        companyNo: user?.companyNo || '001',
        warehouseID: '',
        stcZoneID: '',
        binCode: '',
        description1: '',
        binSizeNo: 0,
        binTypeNo: 0,
        resourceNo: undefined,
        binGrpNo: undefined,
        stcBinCatNo: undefined,
        pickPriorityNo: undefined,
        putAwayPriorityNo: undefined,
        movementTypeNo: undefined,
        customSortID: 0,
        reservationAllowed: true,
        negativeReservationAllowed: false,
        autoReplenishment: false,
        pickableYesNo: true,
        sharedBinYesNo: false,
        sharedBinMaxQuantity: undefined,
        sharedBinPreferEmptyBin: false,
        replenishMinLevel: undefined,
        replenishMaxLevel: undefined,
        itemNo: undefined
    });
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');
    const [showSuccess, setShowSuccess] = useState(false);

    // Convert master data maps to sorted arrays for dropdowns
    const warehouses = masterData ? Array.from(masterData.warehouses.values()).sort((a, b) => a.warehouseID.localeCompare(b.warehouseID)) : [];
    const zones = masterData ? Array.from(masterData.zones.values()).sort((a, b) => a.stcZoneID.localeCompare(b.stcZoneID)) : [];
    const binTypes = masterData ? Array.from(masterData.binTypes.values()).sort((a, b) => a.binTypeNo - b.binTypeNo) : [];
    const binSizes = masterData ? Array.from(masterData.binSizes.values()).sort((a, b) => a.binSizeNo - b.binSizeNo) : [];
    const binGroups = masterData ? Array.from(masterData.binGroups.values()).sort((a, b) => a.binGrpNo - b.binGrpNo) : [];
    const binCategories = masterData ? Array.from(masterData.binCategories.values()).sort((a, b) => a.stcBinCatNo - b.stcBinCatNo) : [];
    const resources = masterData ? Array.from(masterData.resources.values()).sort((a, b) => a.resourceNo - b.resourceNo) : [];
    const priorities = masterData ? Array.from(masterData.priorities.values()).sort((a, b) => a.priorityNo - b.priorityNo) : [];
    const movementTypes = masterData ? Array.from(masterData.movementTypes.values()).sort((a, b) => a.movementTypeNo - b.movementTypeNo) : [];

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.binCode.trim()) {
            setError('Lokasjonskode er obligatorisk');
            return;
        }
        if (!formData.warehouseID.trim()) {
            setError('Lagersted er obligatorisk');
            return;
        }

        try {
            setIsSaving(true);
            setError('');
            await stcBinService.create(formData);

            setShowSuccess(true);
            setTimeout(() => {
                setShowSuccess(false);
                onSuccess();
            }, 1200);
        } catch (err: any) {
            setError(err.message || 'Feil ved opprettelse av lokasjon');
        } finally {
            setIsSaving(false);
        }
    };

    const updateField = <K extends keyof CreateStcBinRequest>(key: K, value: CreateStcBinRequest[K]) => {
        setFormData(prev => ({ ...prev, [key]: value }));
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-hidden">
            <div className="absolute inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose} />

            <div className="absolute inset-0 flex items-center justify-center p-4">
                <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                    {/* Header */}
                    <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-green-600 to-emerald-600 rounded-t-xl">
                        <div>
                            <h2 className="text-lg font-semibold text-white">Ny lokasjon</h2>
                            <p className="text-sm text-green-100">Opprett en ny lagerlokasjon</p>
                        </div>
                        <button onClick={onClose} className="text-white/80 hover:text-white transition-colors">
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Content */}
                    <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-5">
                        {error && (
                            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                                <div className="flex items-start gap-2">
                                    <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                                    <p className="text-sm text-red-800">{error}</p>
                                </div>
                            </div>
                        )}

                        {showSuccess && (
                            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                                <div className="flex items-center gap-2">
                                    <CheckCircle className="w-4 h-4 text-green-600" />
                                    <p className="text-sm text-green-800 font-medium">Lokasjon opprettet!</p>
                                </div>
                            </div>
                        )}

                        {/* Grunndata */}
                        <fieldset className="border border-gray-200 rounded-lg p-4">
                            <legend className="text-sm font-semibold text-gray-700 px-2">Grunndata</legend>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                        Lokasjonskode <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.binCode}
                                        onChange={(e) => updateField('binCode', e.target.value)}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                        placeholder="F.eks. A-01-01"
                                        required
                                        autoFocus
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                        Lagersted <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        value={formData.warehouseID}
                                        onChange={(e) => updateField('warehouseID', e.target.value)}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white"
                                        required
                                    >
                                        <option value="">-- Velg lagersted --</option>
                                        {warehouses.map(w => (
                                            <option key={w.warehouseID} value={w.warehouseID}>
                                                {w.warehouseDescr}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Beskrivelse</label>
                                    <input
                                        type="text"
                                        value={formData.description1 || ''}
                                        onChange={(e) => updateField('description1', e.target.value)}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                        placeholder="Beskrivelse av lokasjonen"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Sone</label>
                                    <select
                                        value={formData.stcZoneID}
                                        onChange={(e) => updateField('stcZoneID', e.target.value)}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white"
                                    >
                                        <option value="">-- Velg sone --</option>
                                        {zones.map(z => (
                                            <option key={z.stcZoneID} value={z.stcZoneID}>
                                                {z.stcZoneID} - {z.description1}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </fieldset>

                        {/* Klassifisering */}
                        <fieldset className="border border-gray-200 rounded-lg p-4">
                            <legend className="text-sm font-semibold text-gray-700 px-2">Klassifisering</legend>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Lokasjonstype</label>
                                    <select
                                        value={formData.binTypeNo}
                                        onChange={(e) => updateField('binTypeNo', parseInt(e.target.value) || 0)}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white"
                                    >
                                        <option value={0}>-- Velg type --</option>
                                        {binTypes.map(t => (
                                            <option key={t.binTypeNo} value={t.binTypeNo}>
                                                {t.binTypeNo} - {t.description1}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Størrelse</label>
                                    <select
                                        value={formData.binSizeNo}
                                        onChange={(e) => updateField('binSizeNo', parseInt(e.target.value) || 0)}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white"
                                    >
                                        <option value={0}>-- Velg størrelse --</option>
                                        {binSizes.map(s => (
                                            <option key={s.binSizeNo} value={s.binSizeNo}>
                                                {s.binSizeNo} - {s.description1}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Lokasjonsgruppe</label>
                                    <select
                                        value={formData.binGrpNo ?? ''}
                                        onChange={(e) => updateField('binGrpNo', e.target.value ? parseInt(e.target.value) : undefined)}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white"
                                    >
                                        <option value="">-- Velg gruppe --</option>
                                        {binGroups.map(g => (
                                            <option key={g.binGrpNo} value={g.binGrpNo}>
                                                {g.binGrpNo} - {g.description1}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Kategori</label>
                                    <select
                                        value={formData.stcBinCatNo ?? ''}
                                        onChange={(e) => updateField('stcBinCatNo', e.target.value ? parseInt(e.target.value) : undefined)}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white"
                                    >
                                        <option value="">-- Velg kategori --</option>
                                        {binCategories.map(c => (
                                            <option key={c.stcBinCatNo} value={c.stcBinCatNo}>
                                                {c.stcBinCatNo} - {c.lng_1}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Ressurs</label>
                                    <select
                                        value={formData.resourceNo ?? ''}
                                        onChange={(e) => updateField('resourceNo', e.target.value ? parseInt(e.target.value) : undefined)}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white"
                                    >
                                        <option value="">-- Velg ressurs --</option>
                                        {resources.map(r => (
                                            <option key={r.resourceNo} value={r.resourceNo}>
                                                {r.resourceNo} - {r.description1}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Bevegelseskode</label>
                                    <select
                                        value={formData.movementTypeNo ?? ''}
                                        onChange={(e) => updateField('movementTypeNo', e.target.value ? parseInt(e.target.value) : undefined)}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white"
                                    >
                                        <option value="">-- Velg bevegelseskode --</option>
                                        {movementTypes.map(m => (
                                            <option key={m.movementTypeNo} value={m.movementTypeNo}>
                                                {m.movementTypeNo} - {m.description1}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Sortering</label>
                                    <input
                                        type="number"
                                        value={formData.customSortID || ''}
                                        onChange={(e) => updateField('customSortID', parseInt(e.target.value) || 0)}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                        placeholder="0"
                                    />
                                </div>
                            </div>
                        </fieldset>

                        {/* Prioritet */}
                        <fieldset className="border border-gray-200 rounded-lg p-4">
                            <legend className="text-sm font-semibold text-gray-700 px-2">Prioritet</legend>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Plukkprioritet</label>
                                    <select
                                        value={formData.pickPriorityNo ?? ''}
                                        onChange={(e) => updateField('pickPriorityNo', e.target.value ? parseInt(e.target.value) : undefined)}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white"
                                    >
                                        <option value="">-- Velg prioritet --</option>
                                        {priorities.map(p => (
                                            <option key={`pick-${p.priorityNo}`} value={p.priorityNo}>
                                                {p.priorityNo} - {p.description1}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Innlagrings-prioritet</label>
                                    <select
                                        value={formData.putAwayPriorityNo ?? ''}
                                        onChange={(e) => updateField('putAwayPriorityNo', e.target.value ? parseInt(e.target.value) : undefined)}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white"
                                    >
                                        <option value="">-- Velg prioritet --</option>
                                        {priorities.map(p => (
                                            <option key={`put-${p.priorityNo}`} value={p.priorityNo}>
                                                {p.priorityNo} - {p.description1}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Plukkrekkefølge</label>
                                    <input
                                        type="text"
                                        value={formData.pickCustomOrder || ''}
                                        onChange={(e) => updateField('pickCustomOrder', e.target.value || undefined)}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                        placeholder="Egendefinert plukkrekkefølge"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Innlagringsrekkefølge</label>
                                    <input
                                        type="text"
                                        value={formData.putAwayCustomOrder || ''}
                                        onChange={(e) => updateField('putAwayCustomOrder', e.target.value || undefined)}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                        placeholder="Egendefinert innlagringsrekkefølge"
                                    />
                                </div>
                            </div>
                        </fieldset>

                        {/* Innstillinger */}
                        <fieldset className="border border-gray-200 rounded-lg p-4">
                            <legend className="text-sm font-semibold text-gray-700 px-2">Innstillinger</legend>
                            <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={formData.pickableYesNo ?? true}
                                        onChange={(e) => updateField('pickableYesNo', e.target.checked)}
                                        className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                                    />
                                    <span className="text-sm text-gray-700">Plukkbar</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={formData.reservationAllowed ?? true}
                                        onChange={(e) => updateField('reservationAllowed', e.target.checked)}
                                        className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                                    />
                                    <span className="text-sm text-gray-700">Reservasjon tillatt</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={formData.negativeReservationAllowed ?? false}
                                        onChange={(e) => updateField('negativeReservationAllowed', e.target.checked)}
                                        className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                                    />
                                    <span className="text-sm text-gray-700">Negativ reservasjon</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={formData.autoReplenishment ?? false}
                                        onChange={(e) => updateField('autoReplenishment', e.target.checked)}
                                        className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                                    />
                                    <span className="text-sm text-gray-700">Automatisk etterfylling</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={formData.sharedBinYesNo ?? false}
                                        onChange={(e) => updateField('sharedBinYesNo', e.target.checked)}
                                        className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                                    />
                                    <span className="text-sm text-gray-700">Felleslokasjon</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={formData.sharedBinPreferEmptyBin ?? false}
                                        onChange={(e) => updateField('sharedBinPreferEmptyBin', e.target.checked)}
                                        className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                                    />
                                    <span className="text-sm text-gray-700">Prioriter tomme felleslok.</span>
                                </label>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Maks ant. på felleslok.</label>
                                    <input
                                        type="number"
                                        value={formData.sharedBinMaxQuantity ?? ''}
                                        onChange={(e) => updateField('sharedBinMaxQuantity', e.target.value ? parseInt(e.target.value) : undefined)}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                        placeholder="0"
                                    />
                                </div>
                            </div>
                        </fieldset>

                        {/* Etterfylling */}
                        <fieldset className="border border-gray-200 rounded-lg p-4">
                            <legend className="text-sm font-semibold text-gray-700 px-2">Etterfylling</legend>
                            <div className="grid grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Artikkel</label>
                                    <input
                                        type="text"
                                        value={formData.itemNo || ''}
                                        onChange={(e) => updateField('itemNo', e.target.value || undefined)}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                        placeholder="Artikkelnr."
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Min. nivå</label>
                                    <input
                                        type="number"
                                        value={formData.replenishMinLevel ?? ''}
                                        onChange={(e) => updateField('replenishMinLevel', e.target.value ? parseInt(e.target.value) : undefined)}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                        placeholder="0"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Maks. nivå</label>
                                    <input
                                        type="number"
                                        value={formData.replenishMaxLevel ?? ''}
                                        onChange={(e) => updateField('replenishMaxLevel', e.target.value ? parseInt(e.target.value) : undefined)}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                        placeholder="0"
                                    />
                                </div>
                            </div>
                        </fieldset>

                        {/* Footer */}
                        <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                                Avbryt
                            </button>
                            <button
                                type="submit"
                                disabled={isSaving}
                                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                                    isSaving
                                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                        : 'bg-green-600 text-white hover:bg-green-700'
                                }`}
                            >
                                {isSaving ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                        Oppretter...
                                    </>
                                ) : (
                                    <>
                                        <Plus className="w-4 h-4" />
                                        Opprett lokasjon
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};
